import React, { useState } from "react";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useMyRecruitmentRequests } from "../../hooks/useRecruitment";
import { useHiringRequisitions } from "../../hooks/useHiringRequisitions";
import { useAuth } from "../../context/AuthContext";
import LeadAssignedCandidates from "./LeadAssignedCandidates";
import CreateHiringRequisitionModal from "../../components/recruitment/CreateHiringRequisitionModal";
import { Plus } from "lucide-react";

export default function LeadRecruitment() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid || userProfile?.id;
  const { data: oldRequests, loading: loadingOld } = useMyRecruitmentRequests(uid);
  const { data: requisitions, loading: loadingReqs } = useHiringRequisitions();

  const [activeTab, setActiveTab] = useState("candidates");
  const [showModal, setShowModal] = useState(false);

  // Combine requisitions belonging to this Team Lead
  const myRequisitions = requisitions.filter(
    (r) => r.createdById === uid || r.requestedById === uid || r.createdByName === userProfile?.displayName
  );
  const allMyRequests = myRequisitions.length > 0 ? myRequisitions : oldRequests;
  const loading = loadingOld && loadingReqs;

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <style>{`
        .lead-req-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); margin-bottom: 1rem; }
        .req-status-pill { padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; }
        .req-status--Submitted { background: #fef3c7; color: #92400e; }
        .req-status--Approved { background: #d1fae5; color: #065f46; }
        .req-status--Converted { background: #e0f2fe; color: #0369a1; }
        .req-status--Rejected { background: #fee2e2; color: #991b1b; }
      `}</style>

      {/* Header & Subtabs */}
      <div className="page-header" style={{ marginBottom: "1rem" }}>
        <div>
          <h1 className="page-title">Team Recruitment & Hiring</h1>
          <p className="page-subtitle">Review assigned candidate profiles, submit technical recommendations, and manage team hiring requests</p>
        </div>
        {activeTab === "requests" && (
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} /> New Hiring Requisition
          </button>
        )}
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="ats-tabs-bar" style={{ display: "flex", gap: "0.5rem", borderBottom: "2px solid var(--border, #e5e7eb)", marginBottom: "1.5rem" }}>
        <button
          className={`ats-tab-btn ${activeTab === "candidates" ? "active" : ""}`}
          style={{ padding: "0.65rem 1.25rem", background: "none", border: "none", fontWeight: activeTab === "candidates" ? 700 : 600, fontSize: "0.86rem", cursor: "pointer", color: activeTab === "candidates" ? "#1b4b31" : "#6b7280", borderBottom: activeTab === "candidates" ? "3px solid #1b4b31" : "3px solid transparent", marginBottom: "-2px" }}
          onClick={() => setActiveTab("candidates")}
        >
          👤 Assigned Candidates
        </button>
        <button
          className={`ats-tab-btn ${activeTab === "requests" ? "active" : ""}`}
          style={{ padding: "0.65rem 1.25rem", background: "none", border: "none", fontWeight: activeTab === "requests" ? 700 : 600, fontSize: "0.86rem", cursor: "pointer", color: activeTab === "requests" ? "#1b4b31" : "#6b7280", borderBottom: activeTab === "requests" ? "3px solid #1b4b31" : "3px solid transparent", marginBottom: "-2px" }}
          onClick={() => setActiveTab("requests")}
        >
          📥 Hiring Requisitions ({allMyRequests.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "candidates" && <LeadAssignedCandidates />}

      {activeTab === "requests" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading your requests...</div>
          ) : allMyRequests.length === 0 ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
              No hiring requisitions submitted yet. Click "+ New Hiring Requisition" to request new team members.
            </div>
          ) : (
            <div>
              {allMyRequests.map((req) => (
                <div key={req.id} className="lead-req-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{req.title || req.positionTitle}</div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>
                        {req.department} · {req.openings || req.vacancies || 1} opening(s) · Priority: {req.priority || "Medium"}
                      </div>
                    </div>
                    <span className={`req-status-pill req-status--${req.status}`}>{req.status}</span>
                  </div>
                  <div style={{ marginTop: "0.75rem", fontSize: "0.82rem", background: "var(--bg-secondary, #f8fafc)", padding: "0.75rem", borderRadius: 8 }}>
                    <strong>Justification:</strong> {req.justification || req.description || "—"}
                  </div>
                  {(req.hrRemarks || req.rejectionReason) && (
                    <div style={{ marginTop: "0.5rem", color: req.status === "Rejected" ? "#ef4444" : "#10b981", fontSize: "0.78rem", fontWeight: 600 }}>
                      HR Feedback: {req.hrRemarks || req.rejectionReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Enterprise Reusable Requisition Modal */}
      <CreateHiringRequisitionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </Layout>
  );
}
